import axios from "axios";
import { PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useAuth } from "../Store/auth-context";
import { useUIContext } from "../Store/ui-context";
import { getBausteine, postBaustein } from "../Utils/api";
import BauSteinUi from "./Ui/BauSteinUi";
import CustomButton from "./Ui/CustomButton";

interface BauStein {
  id: string;
  titel: string;
  inhalt: string;
}

interface BauSteineProps {
  textSelected: (t: string) => void;
}

const TextBauSteine: React.FC<BauSteineProps> = ({ textSelected }) => {
  const [bauSteine, setBauSteine] = useState<BauStein[]>([]);
  const [newBauStein, setNewBauStein] = useState<BauStein>({
    id: "",
    titel: "",
    inhalt: "",
  });
  const { token } = useAuth();
  const { setShowTextbauSteine, showDialog, openErrorModal } = useUIContext();

  const url = "https://qas.freyadv.de/eserviceschein/";

  useEffect(() => {
    loadBausteine();
  }, [token]);

  const loadBausteine = async () => {
    try {
      setBauSteine([]);
      const result = await getBausteine();

      setBauSteine(result);
    } catch (error) {
      console.error("Error loading bausteine:", error);
      setBauSteine([]); // Fallback in case of an error
    }
  };

  const addBauStein = async () => {
    if (!newBauStein.titel.trim() || !newBauStein.inhalt.trim()) {
      openErrorModal(
        "Bitte Titel und Inhalt eingeben.",
        "Speichern nicht möglich"
      );
      return;
    }
    const data = await postBaustein(newBauStein);
    setBauSteine([...bauSteine, data]);
    setNewBauStein({ id: "", titel: "", inhalt: "" }); // Reset input field
  };

  const deleteBauStein = async (id: string) => {
    showDialog({
      title: "Bestätigung",
      message: "Möchten Sie diesen Textbaustein wirklich löschen?",
      confirmButtonText: "Löschen",
      cancelButtonText: "Abbrechen",
      onConfirm: async () => {
        try {
          deleteBauStein(id);

          setBauSteine(bauSteine.filter((bs) => bs.id !== id));
        } catch (error) {
          alert("Fehler beim Löschen. Bitte versuche es später erneut.");
        }
      },
    });
  };

  const handleChangeBausteine = async (
    id: string,
    updatedBaustein: BauStein
  ) => {
    showDialog({
      title: "Sichern",
      message: "Möchten Sie diesen Textbaustein wirklich speichern?",
      confirmButtonText: "Speichern",
      cancelButtonText: "Abbrechen",
      onConfirm: async () => {
        try {
          let body = {
            titel: updatedBaustein.titel,
            inhalt: updatedBaustein.inhalt,
          };

          await axios.post(url + `bausteine/${id}`, body, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${token}`,
            },
          });
        } catch (error) {
          alert("Fehler beim Löschen. Bitte versuche es später erneut.");
        } finally {
          await loadBausteine();
        }
      },
      onCancel: async () => {
        await loadBausteine();
      },
    });
  };

  return (
    <div className="select-none flex flex-col items-center justify-between w-full max-w-4xl mx-auto rounded-2xl bg-white p-4 sm:p-6 md:p-8">
      <label className="select-none col-span-2 w-full text-lg font-medium leading-6 text-center text-blue_royal mb-5">
        Textbausteine
      </label>

      {/* Input for New Textbaustein in One Line */}
      <div className="w-full flex flex-col sm:flex-row items-center bg-gray-200 rounded-lg p-2.5 mb-10 gap-3">
        <input
          type="text"
          placeholder="Bezeichnung"
          value={newBauStein.titel}
          onChange={(e) =>
            setNewBauStein({ ...newBauStein, titel: e.target.value })
          }
          className="input input-bordered w-full sm:w-1/3 rounded-lg pl-2"
        />
        <input
          type="text"
          placeholder="Inhalt vom Textbaustein..."
          value={newBauStein.inhalt}
          onChange={(e) =>
            setNewBauStein({ ...newBauStein, inhalt: e.target.value })
          }
          className="input input-bordered w-full sm:w-2/3 pl-2 rounded-lg"
        />
        <button onClick={addBauStein} className="btn btn-ghost">
          <PlusCircle size={24} />
        </button>
      </div>

      {/* Display List of Textbausteine */}
      <div className="col-span-2 mt-2 items-center justify-center bg-white w-full">
        {bauSteine.map((b: BauStein) => (
          <div
            key={b.id}
            className="flex flex-col whitespace-pre-line items-center w-full mb-2.5"
          >
            <BauSteinUi
              baustein={b}
              onAdd={addBauStein}
              onDelete={deleteBauStein}
              onPress={(text) => {
                textSelected(text);
                setShowTextbauSteine(false);
              }}
              onChange={(id, updatedBaustein) =>
                handleChangeBausteine(id, updatedBaustein)
              }
              //   setBauSteine(
              //     bauSteine.map((bs) => (bs.id === id ? updatedBaustein : bs))
              //   )
              // }
              onCheck={() => {}}
            />
          </div>
        ))}

        {bauSteine.length === 0 && (
          <div className="flex flex-col items-center mb-10">
            <p className="text-lg">Keine Textbausteine vorhanden</p>
          </div>
        )}
      </div>

      {/* Close Button */}
      <div className="col-span-2 mt-4 flex justify-center items-center w-full">
        <CustomButton
          theme="navy"
          className="w-56"
          onClick={() => setShowTextbauSteine(false)}
        >
          Schliessen
        </CustomButton>
      </div>
    </div>
  );
};

export default TextBauSteine;
